import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import {
  AlertCircle, BookOpen, Calendar, Clock, Flame, Heart,
  Moon, Music, Sparkles, Star, Sun, Sunrise, Sunset,
} from 'lucide-react';

/**
 * תבנית צד — לייאוט מפוצל: פאנל כהה בצד ימין עם תאריך ופריטים חשובים,
 * ואזור בהיר ראשי עם שאר הפריטים. לייאוט שונה לחלוטין מרשימה אנכית.
 */

const NAVY = '#162a40';
const NAVY_MID = '#1e3a5f';
const GOLD = '#c9a227';
const IVORY = '#f5f0e8';
const BG = '#faf8f5';
const TEXT_PRIMARY = '#1e293b';
const TEXT_MUTED = '#7a8a9a';
const BORDER = '#e8e0d0';

const CATEGORY_ICONS = {
  tachanun: AlertCircle, hallel: Music, prayerAdditions: BookOpen,
  seasonalPrayers: Sunrise, seasonalTransitions: Sun, selichot: Heart,
  weekdayPrayers: Clock, shabbatAdditions: Flame, motzaeiShabbat: Moon,
  seasonalCustoms: Star, liturgy: Sparkles, leyning: BookOpen,
  haftarah: BookOpen, mourningPeriods: Sunset, general: Calendar,
};

const CATEGORY_LABELS = {
  tachanun: 'תחנון', hallel: 'הלל', prayerAdditions: 'תוספות תפילה',
  seasonalPrayers: 'תפילות עונתיות', seasonalTransitions: 'מעברים עונתיים',
  selichot: 'סליחות', weekdayPrayers: 'תפילות ימי חול',
  shabbatAdditions: 'תוספות שבת', motzaeiShabbat: 'מוצאי שבת',
  seasonalCustoms: 'מנהגים עונתיים', liturgy: 'פיוטים וקריאות',
  leyning: 'קריאת התורה', haftarah: 'הפטרה',
  mourningPeriods: 'ימי אבל ותענית', general: 'כללי',
};

// -- פריט בפאנל הכהה (high priority) ------------------------------------------
const SidebarItem = ({ item }) => {
  const IconComp = CATEGORY_ICONS[item.category] || Calendar;
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 0.75,
      py: 0.5,
      borderBottom: `1px solid rgba(201,162,39,0.15)`,
      '&:last-child': { borderBottom: 'none' },
    }}>
      <IconComp size={12} style={{ color: GOLD, marginTop: 3, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.72rem',
          fontWeight: 700,
          color: GOLD,
          lineHeight: 1.35,
        }}>
          {item.label}
        </Typography>
        {item.value && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.65rem',
            color: IVORY,
            lineHeight: 1.3,
            opacity: 0.8,
          }}>
            {item.value}
          </Typography>
        )}
        {item.detail && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.58rem',
            color: 'rgba(245,240,232,0.5)',
            lineHeight: 1.3,
          }}>
            {item.detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// -- פריט באזור הראשי (normal priority) ----------------------------------------
const MainItem = ({ item }) => {
  const IconComp = CATEGORY_ICONS[item.category] || Calendar;
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 0.75,
      py: 0.4,
    }}>
      <IconComp size={12} style={{ color: TEXT_MUTED, marginTop: 3, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.73rem',
            fontWeight: 600,
            color: TEXT_PRIMARY,
            lineHeight: 1.4,
          }}>
            {item.label}
          </Typography>
          {item.value && (
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, David, serif',
              fontSize: '0.68rem',
              color: TEXT_MUTED,
              lineHeight: 1.4,
            }}>
              {item.value}
            </Typography>
          )}
        </Box>
        {item.detail && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.58rem',
            color: '#b0b8c4',
            lineHeight: 1.3,
          }}>
            {item.detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// -- כותרת קטגוריה באזור הראשי -------------------------------------------------
const MainCategoryHeader = ({ label }) => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    mt: 0.75,
    mb: 0.25,
  }}>
    <Typography sx={{
      fontFamily: 'Frank Ruhl Libre, David, serif',
      fontSize: '0.55rem',
      fontWeight: 700,
      color: NAVY_MID,
      letterSpacing: '0.08em',
      whiteSpace: 'nowrap',
    }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: BORDER }} />
  </Box>
);

// -- עזר: קיבוץ לפי קטגוריה ---------------------------------------------------
function groupByCategory(items) {
  const map = {};
  items.forEach((item) => {
    const cat = item.category || 'general';
    if (!map[cat]) map[cat] = [];
    map[cat].push(item);
  });
  return Object.entries(map);
}

// -- יום lookahead בודד --------------------------------------------------------
const LookaheadDay = ({ dayData }) => {
  const grouped = groupByCategory(dayData.items);
  return (
    <Box sx={{ mt: 1, pt: 0.75, borderTop: `1px dashed ${BORDER}` }}>
      <Typography sx={{
        fontFamily: 'Frank Ruhl Libre, David, serif',
        fontSize: '0.68rem',
        fontWeight: 700,
        color: NAVY_MID,
        mb: 0.4,
      }}>
        {dayData.hebrewDate?.hebrew || dayData.date}
      </Typography>
      {dayData.items.length === 0 ? (
        <Typography sx={{
          fontFamily: 'Frank Ruhl Libre, David, serif',
          fontSize: '0.62rem',
          color: '#94a3b8',
        }}>
          אין ענייני יום מיוחדים
        </Typography>
      ) : (
        grouped.map(([cat, catItems]) => (
          <Box key={cat}>
            <MainCategoryHeader label={CATEGORY_LABELS[cat] || cat} />
            {catItems.map((item, i) => (
              <MainItem key={item.id || `${cat}-${i}`} item={item} />
            ))}
          </Box>
        ))
      )}
    </Box>
  );
};

// == תבנית צד ==================================================================
const Sidebar = ({
  today,
  items,
  lookaheadItems,
  lookaheadDays,
  loading,
  error,
  title,
  showTitle,
}) => {
  const hebrewDateStr = today?.hebrewDate?.hebrew || '';
  const allItems = items || [];

  // מפריד פריטים חשובים לפאנל הצדדי, רגילים למרכז
  const sidebarItems = allItems.filter(it => it.priority === 'high');
  const mainItems = allItems.filter(it => it.priority !== 'high');
  const mainGrouped = groupByCategory(mainItems);

  // אם אין פריטים חשובים, נשים את 2-3 הראשונים בסרגל הצד
  const effectiveSidebar = sidebarItems.length > 0 ? sidebarItems : allItems.slice(0, Math.min(3, allItems.length));
  const effectiveMain = sidebarItems.length > 0 ? mainItems : allItems.slice(Math.min(3, allItems.length));
  const effectiveMainGrouped = sidebarItems.length > 0 ? mainGrouped : groupByCategory(effectiveMain);

  return (
    <Box sx={{
      height: '100%',
      display: 'grid',
      gridTemplateColumns: '35% 1fr',
      direction: 'rtl',
      overflow: 'hidden',
    }}>
      {/* פאנל צד ימני — כהה */}
      <Box sx={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #0f1e30 100%)`,
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 12px',
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: 2 },
        '&::-webkit-scrollbar-thumb': { background: `${GOLD}40`, borderRadius: 1 },
      }}>
        {/* כותרת */}
        {showTitle && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.6rem',
            color: GOLD,
            letterSpacing: '0.14em',
            textAlign: 'center',
            mb: 0.25,
            fontWeight: 600,
          }}>
            {title}
          </Typography>
        )}

        {/* תאריך עברי */}
        {hebrewDateStr && (
          <Box sx={{ textAlign: 'center', mb: 1.25 }}>
            <Typography sx={{
              fontFamily: 'Frank Ruhl Libre, David, serif',
              fontSize: '0.88rem',
              fontWeight: 700,
              color: IVORY,
              lineHeight: 1.3,
            }}>
              {hebrewDateStr}
            </Typography>
            <Box sx={{
              width: 30,
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
              mx: 'auto',
              mt: 0.5,
            }} />
          </Box>
        )}

        {/* loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={14} sx={{ color: GOLD }} />
          </Box>
        )}

        {/* פריטים חשובים / ראשונים */}
        {effectiveSidebar.map((item, i) => (
          <SidebarItem key={item.id || i} item={item} />
        ))}
      </Box>

      {/* אזור ראשי — בהיר */}
      <Box sx={{
        background: BG,
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 12px',
        overflowY: 'auto',
        borderRight: `1px solid ${BORDER}`,
        '&::-webkit-scrollbar': { width: 2 },
        '&::-webkit-scrollbar-thumb': { background: '#d4cfc6', borderRadius: 1 },
      }}>
        {/* error */}
        {error && (
          <Typography sx={{
            fontSize: '0.65rem',
            color: '#ef4444',
            mb: 0.5,
          }}>
            שגיאה בטעינה
          </Typography>
        )}

        {/* ריק */}
        {effectiveMainGrouped.length === 0 && effectiveSidebar.length === 0 && !loading && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.72rem',
            color: '#94a3b8',
            textAlign: 'center',
            mt: 2,
          }}>
            אין ענייני יום מיוחדים
          </Typography>
        )}

        {/* פריטים לפי קטגוריה */}
        {effectiveMainGrouped.map(([cat, catItems]) => (
          <Box key={cat}>
            <MainCategoryHeader label={CATEGORY_LABELS[cat] || cat} />
            {catItems.map((item, i) => (
              <MainItem key={item.id || `${cat}-${i}`} item={item} />
            ))}
          </Box>
        ))}

        {/* אם אין פריטים באזור הראשי אבל יש בצד */}
        {effectiveMainGrouped.length === 0 && effectiveSidebar.length > 0 && !loading && (
          <Typography sx={{
            fontFamily: 'Frank Ruhl Libre, David, serif',
            fontSize: '0.65rem',
            color: '#b0b8c4',
            textAlign: 'center',
            mt: 1.5,
            fontStyle: 'italic',
          }}>
            כל ענייני היום מוצגים בפאנל הצד
          </Typography>
        )}

        {/* ימי lookahead */}
        {lookaheadDays > 0 && lookaheadItems && lookaheadItems.length > 0 && (
          lookaheadItems.map((dayData, i) => (
            <LookaheadDay key={dayData.date || i} dayData={dayData} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
